import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export class TandemError extends Error {
  constructor(
    public status: number,
    detail: unknown,
  ) {
    super(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
}
export async function tandemRequest<T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' = 'GET',
  data?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await apiCaller({
    apiUrl: API_URL,
    path,
    method,
    data,
    signal,
  });
  if (response.status < 200 || response.status >= 300)
    throw new TandemError(response.status, response.result);
  return response.result as T;
}
export type TandemStatus = {
  pair_id: string;
  side: 'legacy' | 'fast';
  other_session_id: number;
  state: string;
  trading_open: boolean;
  control_url: string;
  can_control?: boolean;
};
export type TandemStep = {
  id: string;
  key: string;
  ordinal: number;
  source: string;
  outgoing_date: string;
  target_date: string;
  triggered_at: string;
  legacy_confirmed_at: string | null;
  fast_confirmed_at: string | null;
  both_confirmed_at: string | null;
  legacy_seconds: number | null;
  fast_seconds: number | null;
  legacy_status: string;
  fast_status: string;
  legacy_error: string;
  fast_error: string;
  diagnostics: { id: number; text: string }[];
  recovered: boolean;
  pending_legacy_work: { kind: string; status: string; error: string }[];
};
export type TandemPair = {
  id: string;
  state: string;
  revision: number;
  error: string;
  blueprint: { id: number; name: string; version: number; difficulty: string };
  trainer: { id: number; username: string };
  klass: { id: number; name: string };
  sessions: {
    id: number;
    side: string;
    date: string;
    url: string;
    initialization: string;
  }[];
  server_time: string;
  end_date: string;
  auto_enabled: boolean;
  interval_seconds: number;
  auto_settings_revision: number;
  next_due_at: string | null;
  advance_available: boolean;
  advance_reason: string | null;
  current_step: TandemStep | null;
  steps: TandemStep[];
  continue_available: boolean;
  start_auto_available: boolean;
};
export type AdvanceInput = { key: string; expected_pair_revision: number };
// Keep uncertain requests across refreshes. Reuse their exact input until the
// server resolves them; a new UUID could otherwise create another pair/period.
export function loadPending<T>(key: string): T | null {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}
export function savePending(key: string, input: unknown) {
  if (input === null) sessionStorage.removeItem(key);
  else sessionStorage.setItem(key, JSON.stringify(input));
}
export const seconds = (value: number | null) =>
  value === null ? 'Awaiting confirmation' : `${value.toFixed(2)} s`;
